// shared/src/prompts/registry.ts
// #45 (2026-07-10) — the centralized LLM-step registry.
//
// The "registry" was implicit: 22 prompt-module files each own their STEP /
// MODEL / VERSION / MAX_TOKENS / ROLE consts, and only manifest.ts collected
// them (build-time, for replay correlation). This file joins that authoritative
// roster (PROMPT_MODULES) with the design-lock tier table (_design-lock.ts) into
// ONE descriptor array both the /operations registry tab and any future control
// surface read from. It is a PURE JOIN of things that already exist — no
// per-module edits, so it can never drift from what actually runs.
//
// Phase A of docs/llm-step-registry-2026-07-10-design.md (read-only). The
// per-step llm_calls metrics are joined server-side in the api-worker builder;
// this module is the STATIC half (contract, not runtime metrics).

import { PROMPT_MODULES } from './manifest.js';
import {
  DESIGN_MODEL_LOCK,
  MODEL_DEVIATION_ALLOWLIST,
  type DeepseekModelTier,
} from './_design-lock.js';
import type { DeepseekModel } from '../pricing.js';

export interface LlmStepDescriptor {
  /** Canonical step id (matches the module's STEP + llm_calls.step_name prefix). */
  readonly step:               string;
  /** The model the step ACTUALLY runs (the module's exported MODEL const). */
  readonly model:              DeepseekModel;
  readonly version:            string;
  readonly max_tokens:         number;
  readonly role:               string;
  /** One-line operator description of what the step does (module's DESC const). */
  readonly description:        string;
  /** The design-locked tier from DESIGN_MODEL_LOCK (null if the step is not in
   *  the lock table — should never happen for a live step; surfaced honestly). */
  readonly design_locked_tier: DeepseekModelTier | null;
  readonly design_locked_full: 'deepseek-v4-pro' | 'deepseek-v4-flash' | null;
  /** True when the running MODEL differs from the design lock. */
  readonly is_deviation:       boolean;
  /** True when a deviation is SANCTIONED via MODEL_DEVIATION_ALLOWLIST (has a
   *  Brief-Revision doc). A deviation that is NOT sanctioned is a drift flag. */
  readonly is_sanctioned:      boolean;
  readonly deviation_doc:      string | null;
}

/** The static LLM-step registry — a pure join of PROMPT_MODULES × DESIGN_MODEL_LOCK
 *  × MODEL_DEVIATION_ALLOWLIST. Order follows PROMPT_MODULES (pipeline order). */
export const LLM_STEP_REGISTRY: ReadonlyArray<LlmStepDescriptor> = PROMPT_MODULES.map((m) => {
  const lock  = DESIGN_MODEL_LOCK.find((l) => l.step === m.STEP) ?? null;
  const allow = MODEL_DEVIATION_ALLOWLIST.find((a) => a.step === m.STEP) ?? null;
  const model = m.MODEL as DeepseekModel;
  const is_deviation = lock !== null && model !== lock.designLockedFull;
  return {
    step:               m.STEP,
    model,
    version:            m.VERSION,
    max_tokens:         m.MAX_TOKENS,
    role:               m.ROLE,
    description:        m.DESC,
    design_locked_tier: lock?.designLockedTier ?? null,
    design_locked_full: lock?.designLockedFull ?? null,
    is_deviation,
    // Sanctioned only when the allowlist entry matches the CURRENTLY-running model
    // (a stale allowlist entry for a different model is not a live sanction).
    is_sanctioned:      allow !== null && allow.actualFull === model,
    deviation_doc:      allow?.briefRevisionDoc ?? null,
  };
});

/** Lookup by step id (used by the Phase-B override guard to check the lock). */
export function lookupLlmStep(step: string): LlmStepDescriptor | undefined {
  return LLM_STEP_REGISTRY.find((d) => d.step === step);
}

/** The registry step ids, longest-first isn't needed here — foldStepName picks
 *  the LONGEST matching prefix explicitly. */
const REGISTRY_STEPS: ReadonlyArray<string> = LLM_STEP_REGISTRY.map((d) => d.step);

/**
 * Fold a raw `llm_calls.step_name` onto its registry step id. Runtime step_names
 * carry per-call suffixes at a '.'/':' boundary (`q.1.qa:<qid>`, `p.arbiter.<ticker>`,
 * `lht.thesis.<scope>`, `q.rank.probe:<brief>:<basket>`), so the raw name is matched
 * to the LONGEST registry step that is either an exact match or a boundary-prefix.
 * Returns null when the name matches no registered step (an unregistered call).
 * Shared by the api-worker metrics rollup AND the transport override resolver so
 * both fold identically.
 */
export function foldStepName(raw: string): string | null {
  let best: string | null = null;
  for (const s of REGISTRY_STEPS) {
    if (raw === s) return s;
    if ((raw.startsWith(s + '.') || raw.startsWith(s + ':')) && (best === null || s.length > best.length)) {
      best = s;
    }
  }
  return best;
}

/**
 * #45 Phase B — the TRANSPORT-boundary model resolver. Given a RAW `step_name`
 * (possibly suffixed) as it reaches an LLM transport, the prompt-module's default
 * model, and the operator override map, return the model this call should run.
 *
 * Two carve-outs vs the bare {@link resolveStepModel}:
 *   1. **Shadow A/B challenger rows are NEVER overridden.** A step_name containing
 *      `.shadow-` (e.g. `p.arbiter.SPY.shadow-pro`) is the fixed A/B baseline — its
 *      model is the experiment's control and must not follow a live-step override,
 *      or the equivalence test is corrupted. Returns `moduleDefault` unchanged.
 *   2. **Unregistered / unfoldable names** fall through to `moduleDefault` (no key
 *      to look up), never throwing.
 * Otherwise the raw name is folded to its registry step and the override applied.
 */
export function resolveTransportModel(
  rawStepName:   string,
  moduleDefault: DeepseekModel,
  overrides:     Readonly<Record<string, string>> | undefined,
): DeepseekModel {
  if (rawStepName.includes('.shadow-')) return moduleDefault;
  const step = foldStepName(rawStepName);
  if (step === null) return moduleDefault;
  return resolveStepModel(step, moduleDefault, overrides);
}

/**
 * #45 Phase B — resolve the model a step should run THIS call: an operator's
 * `LLM_STEP_MODEL_OVERRIDE[step]` if set to a canonical model, else the prompt
 * module's own MODEL default. Pure — no DB, no side effects. Values are already
 * enum-validated by ParamsSchema, but we re-guard here so a hand-built override
 * map (tests) can't inject a non-model string. Read from the per-run params
 * snapshot at stage entry, so a brief already in flight never hot-swaps model.
 */
export function resolveStepModel(
  step:          string,
  moduleDefault: DeepseekModel,
  overrides:     Readonly<Record<string, string>> | undefined,
): DeepseekModel {
  const ov = overrides?.[step];
  return ov === 'deepseek-v4-pro' || ov === 'deepseek-v4-flash' ? ov : moduleDefault;
}
